import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Typography,
  Grid,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Close, RemoveRedEyeOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { EditIcon } from "../comps/CustomIcons";
import {
  addPharmacyRequestAcceptByIdApi,
  addPharmacyRequestRejectByIdApi,
  getPharmacyRequestByIdApi,
  updatePharmacyIssuedQuantitiesApi,
} from "../services/medicine-tracking.service";
import { useGetApi } from "../hooks/useGetApi";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { CheckIcon, XIcon } from "lucide-react";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const EXCLUDE_LABEL = [
  {
    id: 1,
    label: "Sl. No.",
    value: "slNo",
  },
  {
    id: 2,
    label: "Item Code",
    value: "itemCode",
  },
  {
    id: 3,
    label: "Medicine Name",
    value: "medicineName",
  },
  {
    id: 4,
    label: "Current Stock",
    value: "currentStock",
  },
  {
    id: 5,
    label: "Requested Quantity",
    value: "requestedQuantity",
  },
  {
    id: 6,
    label: "Issued Quantity",
    value: "issuedQuantity",
  },
  {
    id: 7,
    label: "",
    value: "",
  },
];

const PharmacyMedicineRequest = () => {
  const { trackingId } = useParams();
  const navigate = useNavigate();

  // Validate trackingId
  useEffect(() => {
    if (!trackingId || trackingId === 'undefined' || trackingId === 'null') {
      console.error("Invalid trackingId:", trackingId);
      alert("Invalid request. Please go back and try again.");
      navigate(-1);
    }
  }, [trackingId, navigate]);

  const [excludedDepartments, setExcludedDepartments] = useState([
    {
      itemCode: "",
      medicineName: "",
      currentStock: "",
      quantity: "",
      issuedQuantity: "",
    },
  ]);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Fetch data manually since useGetApi is designed for array responses
  useEffect(() => {
    const fetchData = async () => {
      if (!trackingId) return;
      
      setIsLoading(true);
      setIsError(false);
      
      try {
        const response = await getPharmacyRequestByIdApi({ trackingId });
        
        if (
          response?.isSuccess ||
          response?.success ||
          response?.code === 200
        ) {
          setData(response.data?.data); // Access the nested data structure
        } else {
          setIsError(true);
          console.error("API Error:", response);
        }
      } catch (error) {
        setIsError(true);
        console.error("Fetch Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [trackingId]);

  const basicInfo = data?.basicInfo || {};
  const patientInfo = data?.patientInfo || {};
  const [pharmacyItems, setPharmacyItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});

  const handleAccept = async () => {
    let response;
    const payload = {
      medicines: pharmacyItems.map((item) => ({
        medicineId: item.medicineId,
        issuedQuantity: item.issuedQuantity,
      })),
    };

    setLoading(true);
    response = await addPharmacyRequestAcceptByIdApi(trackingId, payload);
    setLoading(false);

    if (
      response?.isSuccess ||
      response?.success ||
      response?.code === 200 ||
      response?.code === 201
    ) {
      toast.success("Medicine request accepted successfully");
      // Navigate back to pharmacy page after successful acceptance
      setTimeout(() => {
        navigate('/pharmacy');
      }, 1500); // Wait 1.5 seconds to show the success message
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      const response = await addPharmacyRequestRejectByIdApi(trackingId, {
        reason: "Request rejected by pharmacist"
      });
      setLoading(false);

      if (
        response?.isSuccess ||
        response?.success ||
        response?.code === 200 ||
        response?.code === 201
      ) {
        toast.success("Medicine request rejected successfully");
        // Navigate back to pharmacy page after successful rejection
        setTimeout(() => {
          navigate('/pharmacy');
        }, 1500); // Wait 1.5 seconds to show the success message
      } else {
        toast.error(response?.message || "Some error occurred while rejecting.");
      }
    } catch (error) {
      setLoading(false);
      toast.error("Failed to reject medicine request. Please try again.");
    }
  };

  const handleIssuedQuantityChange = (e, index) => {
    // Get the input value
    const inputValue = e.target.value;

    // If the input is empty, set issuedQuantity to empty string
    if (inputValue === "") {
      setPharmacyItems((prevItems) =>
        prevItems.map((item, i) =>
          i === index ? { ...item, issuedQuantity: "" } : item
        )
      );
      return;
    }

    // Convert input to number
    let value = Number(inputValue);

    // Get current stock and requested quantity as numbers
    const currentStock = Number(pharmacyItems[index]?.currentStock) || 0;
    const requestedQuantity =
      Number(pharmacyItems[index]?.requestedQuantity) || 0;

    // Enforce limits directly on the input value
    if (requestedQuantity > 0 && value > requestedQuantity) {
      value = requestedQuantity;
    }

    if (currentStock > 0 && value > currentStock) {
      value = Math.min(value, currentStock);
    }

    // Update the state with the validated value
    setPharmacyItems((prevItems) =>
      prevItems.map((item, i) =>
        i === index ? { ...item, issuedQuantity: value } : item
      )
    );
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Save changes
      handleSaveChanges();
    } else {
      // Start editing
      setIsEditing(true);
      setEditedData({
        basicInfo: { ...basicInfo },
        patientInfo: { ...patientInfo }
      });
    }
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      
      const payload = {
        medicines: pharmacyItems.map((item) => ({
          medicineId: item.medicineId,
          issuedQuantity: item.issuedQuantity,
        })),
      };

      const response = await updatePharmacyIssuedQuantitiesApi(trackingId, payload);

      if (response?.code === 200 || response?.code === 201) {
        toast.success("Issued quantities saved successfully!");
        setIsEditing(false);
        setEditedData({});
      } else {
        toast.error(response?.message || "Failed to save changes.");
      }
    } catch (error) {
      toast.error("Failed to save changes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedData({});
  };

  const handleFieldChange = (section, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  useEffect(() => {
    if (data) {
      const items = data?.pharmacyItems || [];
      setPharmacyItems(items);
    }
  }, [data]);
  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              padding: 2,
              marginTop: 2,
              borderRadius: 2,
              marginRight: "90px",
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 0,
                mb: "20px",
                color: "#5D6A82",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Open Sans",
                  fontWeight: 600,
                  fontSize: "20px",
                  lineHeight: "29px",
                  letterSpacing: "0px",
                  color: "inherit",
                }}
              >
                Material In/Out Issue
              </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              {!isEditing ? (
                <Button
                  variant="outlined"
                  onClick={handleEditToggle}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    minWidth: "120px",
                  }}
                >
                  Edit Details
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      minWidth: "120px",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleEditToggle}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      minWidth: "120px",
                    }}
                  >
                    {loading ? <CircularProgress size={20} /> : "Save Changes"}
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Patient Information */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage 
              message="Some error occurred in Pharmacy. Please try again or contact support if the issue persists."
              onRetry={() => window.location.reload()}
            />
          ) : (
            <>
              <Box
                sx={{
                  border: `1px solid #CCD4DA`,
                  borderRadius: "12px",
                  p: "20px",
                  mb: "30px",
                }}
              >
                <Typography sx={{ color: "#5D6A82", mb: 2, fontSize: "14px" }}>
                  Patient Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="UHID"
                      value={patientInfo?.uhid || "-"}
                      fullWidth
                      required
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Patient Name"
                      value={patientInfo?.patientName || "-"}
                      fullWidth
                      required
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Ward Name"
                      value={patientInfo?.wardName || "-"}
                      fullWidth
                      required
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Bed Number"
                      value={patientInfo?.bedNumber || "-"}
                      fullWidth
                      required
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Basic Information */}
              <Box
                sx={{
                  backgroundColor: "rgba(240, 247, 252, 1)",
                  borderRadius: "12px",
                  mb: "30px",
                  p: "20px",
                }}
              >
                <Typography sx={{ mb: "20px", color: "rgba(93, 106, 130, 1)" }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  {/* inOutType */}
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="In/Out Type"
                      name="inOutType"
                      required
                      fullWidth
                      value={basicInfo?.inOutType || "-"}
                      disabled
                      sx={{ bgcolor: "white" }}
                    />
                  </Grid>

                  {/* inOutDate */}
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <DatePicker
                      label="In/Out Date"
                      name="inOutDate"
                      slotProps={{
                        textField: { required: true, fullWidth: true },
                      }}
                      value={basicInfo?.inOutDate ? dayjs(basicInfo.inOutDate) : null}
                      disabled
                      sx={{ bgcolor: "white" }}
                    />
                  </Grid>

                  {/* priority */}
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Invoice Number"
                      name="invoiceNumber"
                      value={basicInfo?.invoiceNumber || "-"}
                      disabled
                      required
                      fullWidth
                      sx={{ bgcolor: "white" }}
                    />
                  </Grid>

                  {/* remark */}
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <TextField
                      label="Priority"
                      name="priority"
                      value={basicInfo?.priority || "-"}
                      disabled
                      required
                      fullWidth
                      sx={{ bgcolor: "white" }}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Invoice Number */}
              <Typography sx={{ mb: "30px", fontWeight: 600 }}>
                Invoice Number : {basicInfo?.invoiceNumber}
              </Typography>

              <TableContainer component={Paper} sx={{ mb: "30px" }}>
                <Table>
                  <TableHead
                    sx={{
                      bgcolor: "#EAEAEA",
                    }}
                  >
                    <TableRow>
                      {EXCLUDE_LABEL?.map((option) => (
                        <TableCell
                          key={option?.id}
                          sx={{
                            textAlign: option?.align || "left",
                            color: "#5D6A82",
                          }}
                        >
                          {option?.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pharmacyItems?.map((row, index) => (
                      <TableRow key={row?.medicineId || index}>
                        {/* Column 1 */}
                        <TableCell sx={{ color: "#5D6A82" }}>
                          <Typography>{row?.sNo || ""}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#5D6A82" }}>
                          <Typography>{row?.itemCode || ""}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#5D6A82" }}>
                          <Typography>{row?.itemName || ""}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#5D6A82" }}>
                          <Typography>{row?.currentStock}</Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#5D6A82" }}>
                          <Typography>
                            {row?.requestedQuantity || ""}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: "#5D6A82" }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <TextField
                              type="number"
                              value={row?.issuedQuantity}
                              onChange={(e) =>
                                handleIssuedQuantityChange(e, index)
                              }
                                                             disabled={row?.status === "Delivered" || !isEditing}
                              onFocus={(e) => {
                                // Clear the field if it's 0
                                if (
                                  row?.issuedQuantity === 0 ||
                                  row?.issuedQuantity === "0"
                                ) {
                                  // Create a temporary copy of the current items
                                  const updatedItems = [...pharmacyItems];
                                  // Update the specific item
                                  updatedItems[index] = {
                                    ...updatedItems[index],
                                    issuedQuantity: "",
                                  };
                                  // Set the updated items
                                  setPharmacyItems(updatedItems);
                                }
                              }}
                              onBlur={() =>
                                setPharmacyItems((prevItems) =>
                                  prevItems.map((item, i) =>
                                    i === index
                                      ? {
                                          ...item,
                                          issuedQuantity:
                                            item.issuedQuantity === ""
                                              ? 0
                                              : item.issuedQuantity,
                                        }
                                      : item
                                  )
                                )
                              }
                              InputProps={{
                                inputProps: {
                                  min: 0,
                                  max: Math.min(
                                    Number(row?.currentStock) || 0,
                                    Number(row?.requestedQuantity) || 0
                                  ),
                                },
                              }}
                              fullWidth
                            />
                          </Box>
                        </TableCell>

                        {/* Column 3: Action Buttons */}

                        {/* <TableCell align="right" sx={{ color: "#5D6A82" }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {editableRow === index ? (
                              <>
                                <IconButton onClick={handleSave}>
                                  <CheckIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                  onClick={() => setEditableRow(null)}
                                >
                                  <Close sx={{ fontSize: 18 }} />
                                </IconButton>
                              </>
                            ) : (
                              <IconButton
                                onClick={() => handleEditClick(index)}
                              >
                                <EditIcon
                                  sx={{
                                    cursor: "pointer",
                                    color: "transparent",
                                    fontSize: "18px",
                                  }}
                                />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell> */}
                      </TableRow>
                    ))}

                    {/* <TableRow>
                  <TableCell
                    colSpan={EXCLUDE_LABEL?.length}
                    sx={{ color: "#92A6CC", cursor: "pointer" }}
                    onClick={() =>
                      handleAddTableRow(
                        excludedDepartments,
                        setExcludedDepartments,
                        {}
                      )
                    }
                  >
                    + Add item
                  </TableCell>
                </TableRow> */}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* accept and reject buttons */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              type="submit"
              startIcon={<CheckIcon size={20} />}
              sx={{
                minWidth: "150px",
                textTransform: "none",
              }}
              disabled={loading}
              onClick={handleAccept}
            >
              {loading ? <CircularProgress size={24} /> : `Accept`}
            </Button>
            
            <Button
              variant="contained"
              type="submit"
              startIcon={<XIcon size={20} />}
              sx={{
                minWidth: "150px",
                textTransform: "none",
              }}
              disabled={loading}
              onClick={handleReject}
            >
              {loading ? <CircularProgress size={24} /> : `Reject`}
            </Button>
          </Box>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default PharmacyMedicineRequest;
